import {
    eachDayOfInterval,
    endOfWeek,
    format,
    formatISO,
    parseISO,
    startOfWeek,
} from "date-fns";
import { formatDateForTransaction } from "@helpers/date";

const WEEK_STARTS_ON = 0;
const DAYS_IN_WEEK = 7;

export interface CalendarHeatmapDatum {
    date: string;
    value: number;
}

export interface CalendarHeatmapCell {
    date: string;
    value: number;
    isPlaceholder: boolean;
}

export interface CalendarHeatmapWeek {
    cells: CalendarHeatmapCell[];
    monthLabel: string | null;
}

export function getHeatmapIntensityLevel(
    value: number,
    maxValue: number,
): 0 | 1 | 2 | 3 | 4 {
    if (value <= 0 || maxValue <= 0) {
        return 0;
    }

    const ratio = value / maxValue;
    if (ratio <= 0.25) {
        return 1;
    }
    if (ratio <= 0.5) {
        return 2;
    }
    if (ratio <= 0.75) {
        return 3;
    }
    return 4;
}

export function getDefaultHeatmapCellLabel(cell: {
    date: string;
    value: number;
}): string {
    const date = parseISO(`${cell.date}T12:00:00`);
    return `${cell.value} on ${formatDateForTransaction(date)}`;
}

function toDateOnly(date: Date): string {
    return formatISO(date, { representation: "date" });
}

function getMonthLabelForWeek(
    cells: CalendarHeatmapCell[],
    previousMonth: string | null,
): { monthLabel: string | null; monthKey: string | null } {
    const monthStart = cells.find(
        (cell) => !cell.isPlaceholder && cell.date.endsWith("-01"),
    );
    if (monthStart) {
        return {
            monthLabel: format(parseISO(monthStart.date), "MMM"),
            monthKey: monthStart.date.slice(0, 7),
        };
    }

    const firstVisible = cells.find((cell) => !cell.isPlaceholder);
    const monthKey = firstVisible?.date.slice(0, 7) ?? null;
    if (!firstVisible || monthKey === previousMonth) {
        return { monthLabel: null, monthKey };
    }

    return {
        monthLabel: format(parseISO(firstVisible.date), "MMM"),
        monthKey,
    };
}

export function buildCalendarHeatmapWeeks(params: {
    startDate: string;
    endDate: string;
    data: readonly CalendarHeatmapDatum[];
}): CalendarHeatmapWeek[] {
    const valuesByDate = new Map<string, number>();
    for (const item of params.data) {
        valuesByDate.set(item.date, item.value);
    }

    const start = parseISO(params.startDate);
    const end = parseISO(params.endDate);
    const days = eachDayOfInterval({
        start: startOfWeek(start, { weekStartsOn: WEEK_STARTS_ON }),
        end: endOfWeek(end, { weekStartsOn: WEEK_STARTS_ON }),
    });

    const weeks: CalendarHeatmapWeek[] = [];
    let previousMonth: string | null = null;

    for (let index = 0; index < days.length; index += DAYS_IN_WEEK) {
        const weekDays = days.slice(index, index + DAYS_IN_WEEK);
        const cells = weekDays.map((day) => {
            const date = toDateOnly(day);
            const isPlaceholder =
                date < params.startDate || date > params.endDate;
            return {
                date,
                value: isPlaceholder ? 0 : (valuesByDate.get(date) ?? 0),
                isPlaceholder,
            };
        });

        const { monthLabel, monthKey } = getMonthLabelForWeek(
            cells,
            previousMonth,
        );
        if (monthKey) {
            previousMonth = monthKey;
        }

        weeks.push({ cells, monthLabel });
    }

    return weeks;
}
